import {Table, TableHeader, TableBody, TableHead, TableRow, TableCell} from '../../components/table';
import {Button} from '../../components/button';
import {Badge} from '../../components/badge';
import {RefreshCw, ArrowUp, ArrowDown} from 'lucide-react';
import {CommonPagination, ScrollableContentLayout} from '../../components/common';
import {useGetAttendanceList} from './hooks/useGetAttendanceList';
import {useAttendanceStatistics} from './hooks/useAttendanceStatistics';
import type {AttendanceFilters} from './types/attendance.types';
import {useTranslation} from 'react-i18next';
import {useState, useCallback, type Dispatch, type SetStateAction} from 'react';
import StablePopoverTextFilterComponent from '../../components/customs/table-header/popover-text-filter';
import PopoverDateFilterComponent from '../../components/customs/table-header/popover-date-filter';
import PopoverNumberFilterComponent from '../../components/customs/table-header/popover-number-filter';
import PopoverSingleSelectFilterComponent from '../../components/customs/table-header/popover-single-select-filter';
import PopoverTimeFilterV2Component from '../../components/customs/table-header/popover-time-filter-v2';

const AttendancePage = () => {
    const {t} = useTranslation();
    const {
        attendances,
        loading,
        error,
        filters,
        setFilters,
        page,
        setPage,
        totalCount,
        hasActiveFilters,
        refreshAttendances
    } = useGetAttendanceList();

    const {
        data: attendanceStats,
        refresh: refreshStats
    } = useAttendanceStatistics();

    const handlePageChange = (newPage: number) => {
        setPage(newPage);
    };

    const handleRefreshAll = async () => {
        await Promise.all([refreshAttendances(), refreshStats()]);
    };

    // State for managing active filter popup
    const [activeFilterPopup, setActiveFilterPopup] = useState<keyof AttendanceFilters | null>(null);

    // Wrapper function to match TableFilterState interface
    const updateFilter = useCallback<Dispatch<SetStateAction<AttendanceFilters>>>((newFilters) => {
        setFilters(newFilters);
    }, [setFilters]);

    // Helper function to check if a column has active filter
    const hasActiveFilter = useCallback((filterKey: keyof AttendanceFilters) => {
        const value = filters[filterKey];
        if (filterKey === 'rewarded_points') {
            return value !== null && value !== undefined;
        }
        return typeof value === 'string' && value.trim() !== '';
    }, [filters]);

    // Helper function to check if any filter is active
    const hasAnyActiveFilter = useCallback(() => {
        return Object.entries(filters).some(([key, value]) => {
            if (key === 'rewarded_points') {
                return value !== null && value !== undefined;
            }
            return typeof value === 'string' && value.trim() !== '';
        });
    }, [filters]);

    // Helper function to get TableHead classes based on filter state
    const getTableHeadClasses = useCallback((filterKey?: keyof AttendanceFilters) => {
        const baseClasses = "relative transition-colors duration-200";
        return baseClasses;
    }, []);

    // Helper function to get text color based on filter state
    const getTableHeadTextClasses = useCallback((filterKey?: keyof AttendanceFilters) => {
        if (filterKey && hasActiveFilter(filterKey)) {
            return "text-blue-700 dark:text-blue-300 font-medium";
        }
        return "";
    }, [hasActiveFilter]);

    // Helper functions for managing popup state
    const isFilterPopupOpen = useCallback((filterKey: keyof AttendanceFilters) => {
        return activeFilterPopup === filterKey;
    }, [activeFilterPopup]);

    const handleFilterPopupChange = useCallback((filterKey: keyof AttendanceFilters, open: boolean) => {
        if (open) {
            setActiveFilterPopup(filterKey);
        } else {
            setActiveFilterPopup(null);
        }
    }, []);

    const formatDate = (dateString: string) => {
        if (!dateString) return '-';
        const date = new Date(dateString);

        return date.toLocaleDateString("en-US", {
            year: "numeric",
            month: "2-digit",
            day: "2-digit",
        });
    };

    const formatTime = (timeString: string) => {
        if (!timeString) return '-';
        const date = new Date(timeString);
        return date.toLocaleTimeString('en-US', {
            hour: '2-digit',
            minute: '2-digit'
        });
    };

    const getStatusBadge = (status: string) => {
        switch (status) {
            case 'signed_out':
                return <Badge variant="secondary"
                              className="font-semibold bg-[#F3F4F6] text-[#212936] dark:bg-gray-700 dark:text-gray-200">Checked
                    out</Badge>;
            case 'registered':
                return <Badge variant="secondary"
                              className="font-semibold bg-[#DBEAFE] text-[#1E40AF] dark:bg-blue-800 dark:text-blue-200">Attending</Badge>;
            case 'signed_in':
                return <Badge variant="secondary"
                              className="font-semibold bg-[#DCFCE7] text-[#166534] dark:bg-green-800 dark:text-green-200">Checked
                    in</Badge>;
            default:
                return <Badge variant="secondary">{status}</Badge>;
        }
    };

    const getAttendeeTypeBadge = (userType: string) => {
        switch (userType) {
            case 'user':
                return <Badge variant="secondary"
                              className="font-semibold bg-[#DBEAFE] text-[#1E40AF] dark:bg-blue-800 dark:text-blue-200">
                    {t('attendance.user_types.user')}
                </Badge>;
            case 'guest':
                return <Badge variant="secondary"
                              className="font-semibold bg-[#F3E8FF] text-[#7C3AED] dark:bg-purple-800 dark:text-purple-200">
                    {t('attendance.user_types.guest')}
                </Badge>;
            default:
                return <Badge variant="secondary">{userType || '-'}</Badge>;
        }
    };

    // Calculate monthly attendance rate with trend
    const getMonthlyAttendanceData = () => {
        if (attendanceStats?.monthly_attendance_rate) {
            const {current_rate, change_percentage, trend} = attendanceStats.monthly_attendance_rate;
            const TrendIcon = trend === 'up' ? ArrowUp : trend === 'down' ? ArrowDown : null;
            const trendColor = trend === 'up' ? 'text-emerald-600' : trend === 'down' ? 'text-red-600' : 'text-gray-600';

            return {
                value: `${current_rate.toFixed(1)}%`,
                description: TrendIcon ? (
                    <div className={`flex items-center gap-1 text-sm ${trendColor} mt-1`}>
                        <TrendIcon className="h-4 w-4"/>
                        <span>{Math.abs(change_percentage).toFixed(1)}% from last month</span>
                    </div>
                ) : null
            };
        }
        // Default fallback when API data is not available
        return {
            value: '0%',
            description: (
                <div className="flex items-center gap-1 text-sm text-gray-500 mt-1">
                    <span>No data available</span>
                </div>
            )
        };
    };

    // Calculate weekly attendance rate with trend
    const getWeeklyAttendanceData = () => {
        if (attendanceStats?.weekly_attendance_rate) {
            const {current_rate, change_percentage, trend} = attendanceStats.weekly_attendance_rate;
            const TrendIcon = trend === 'up' ? ArrowUp : trend === 'down' ? ArrowDown : null;
            const trendColor = trend === 'up' ? 'text-emerald-600' : trend === 'down' ? 'text-red-600' : 'text-gray-600';

            return {
                value: `${current_rate.toFixed(1)}%`,
                description: TrendIcon ? (
                    <div className={`flex items-center gap-1 text-sm ${trendColor} mt-1`}>
                        <TrendIcon className="h-4 w-4"/>
                        <span>{Math.abs(change_percentage).toFixed(1)}% from last week</span>
                    </div>
                ) : null
            };
        }
        // Default fallback when API data is not available
        return {
            value: '0%',
            description: (
                <div className="flex items-center gap-1 text-sm text-gray-500 mt-1">
                    <span>No data available</span>
                </div>
            )
        };
    };

    const monthlyData = getMonthlyAttendanceData();
    const weeklyData = getWeeklyAttendanceData();

    const stats = [
        {
            label: t('attendance.monthly_attendance_rate'),
            value: monthlyData.value,
            description: monthlyData.description,
            icon: (<div className="w-6 h-6 p-1 rounded-md flex items-center justify-center bg-[#F2FCFA] dark:bg-emerald-900/30">
                <svg width="18" height="17" viewBox="0 0 18 17" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M6.36719 1.75415V4.46248" stroke="#00C49A" strokeWidth="1.35" strokeLinecap="round"
                          strokeLinejoin="round"/>
                    <path d="M11.7852 1.75415V4.46248" stroke="#00C49A" strokeWidth="1.35" strokeLinecap="round"
                          strokeLinejoin="round"/>
                    <path
                        d="M13.8158 3.1084H4.33659C3.5887 3.1084 2.98242 3.71468 2.98242 4.46257V13.9417C2.98242 14.6896 3.5887 15.2959 4.33659 15.2959H13.8158C14.5636 15.2959 15.1699 14.6896 15.1699 13.9417V4.46257C15.1699 3.71468 14.5636 3.1084 13.8158 3.1084Z"
                        stroke="#00C49A" strokeWidth="1.35" strokeLinecap="round" strokeLinejoin="round"/>
                    <path d="M2.98242 7.1709H15.1699" stroke="#00C49A" strokeWidth="1.35" strokeLinecap="round"
                          strokeLinejoin="round"/>
                </svg>
            </div>)
        },
        {
            label: t('attendance.weekly_attendance_rate'),
            value: weeklyData.value,
            description: weeklyData.description,
            icon: (<div className="w-6 h-6 p-1 rounded-md flex items-center justify-center bg-[#F2FCFA] dark:bg-emerald-900/30">
                <svg width="18" height="17" viewBox="0 0 18 17" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path
                        d="M13.8158 3.1084H4.33659C3.5887 3.1084 2.98242 3.71468 2.98242 4.46257V13.9417C2.98242 14.6896 3.5887 15.2959 4.33659 15.2959H13.8158C14.5636 15.2959 15.1699 14.6896 15.1699 13.9417V4.46257C15.1699 3.71468 14.5636 3.1084 13.8158 3.1084Z"
                        stroke="#00C49A" strokeWidth="1.35" strokeLinecap="round" strokeLinejoin="round"/>
                    <path d="M11.7852 1.75415V4.46248" stroke="#00C49A" strokeWidth="1.35" strokeLinecap="round"
                          strokeLinejoin="round"/>
                    <path d="M2.98242 7.1709H15.1699" stroke="#00C49A" strokeWidth="1.35" strokeLinecap="round"
                          strokeLinejoin="round"/>
                    <path d="M6.36719 1.75415V4.46248" stroke="#00C49A" strokeWidth="1.35" strokeLinecap="round"
                          strokeLinejoin="round"/>
                    <path d="M12.4609 9.87915H8.39844" stroke="#00C49A" strokeWidth="1.35" strokeLinecap="round"
                          strokeLinejoin="round"/>
                    <path d="M9.75391 12.5875H5.69141" stroke="#00C49A" strokeWidth="1.35" strokeLinecap="round"
                          strokeLinejoin="round"/>
                    <path d="M5.69141 9.87915H5.69818" stroke="#00C49A" strokeWidth="1.35" strokeLinecap="round"
                          strokeLinejoin="round"/>
                    <path d="M12.4609 12.5875H12.4677" stroke="#00C49A" strokeWidth="1.35" strokeLinecap="round"
                          strokeLinejoin="round"/>
                </svg>
            </div>)
        }
    ];

    const totalPages = Math.ceil(totalCount / 20);

    if (error) {
        return (
            <ScrollableContentLayout
                title={t('attendance.title')}
                subtitle={t('attendance.subtitle')}
                stats={stats}
            >
                <div className="flex justify-center items-center h-64">
                    <div className="text-center">
                        <p className="text-red-600 mb-4">{error}</p>
                        <Button onClick={handleRefreshAll}>{t('ui.retry')}</Button>
                    </div>
                </div>
            </ScrollableContentLayout>
        );
    }

    return (
        <ScrollableContentLayout
            title={t('attendance.title')}
            subtitle={t('attendance.subtitle')}
            stats={stats}
        >
            <div className="flex justify-between items-center mb-4">
                <div className="text-xl font-semibold">{t('attendance.attendances_list')}</div>
                <Button
                    variant="outline"
                    size="sm"
                    onClick={handleRefreshAll}
                    disabled={loading || !hasActiveFilters}
                    className="flex items-center gap-2"
                >
                    <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`}/>
                    {t('ui.refresh')}
                </Button>
            </div>
            
            <div className="flex flex-col flex-1">
                <div className="flex-1 min-h-0 overflow-visible pb-8">
                    <Table containerClassName="overflow-visible">
                        <TableHeader
                            className="[&_th]:sticky [&_th]:top-[100px] [&_th]:bg-white [&_th]:dark:bg-gray-900 [&_th]:z-20 [&_th]:shadow-[0_2px_4px_rgba(0,0,0,0.1)]">
                            <TableRow>
                                <TableHead className={getTableHeadClasses('event')}>
                                    <div className="flex items-center">
                                        <span className={getTableHeadTextClasses('event')}>
                                            {t('attendance.event')}
                                        </span>
                                        <StablePopoverTextFilterComponent
                                            filterState={{
                                                filterKey: 'event',
                                                filter: filters,
                                                setFilter: updateFilter
                                            }}
                                            placeholder={`Search ${t('attendance.event').toLowerCase()}...`}
                                            title={`FILTER BY ${t('attendance.event').toUpperCase()}`}
                                            description="Results update as you type"
                                            isOpen={isFilterPopupOpen('event')}
                                            onOpenChange={(open) => handleFilterPopupChange('event', open)}
                                        />
                                    </div>
                                </TableHead>
                                <TableHead className={getTableHeadClasses('user')}>
                                    <div className="flex items-center">
                                        <span className={getTableHeadTextClasses('user')}>
                                            {t('attendance.user')}
                                        </span>
                                        <StablePopoverTextFilterComponent
                                            filterState={{
                                                filterKey: 'user',
                                                filter: filters,
                                                setFilter: updateFilter
                                            }}
                                            placeholder={`Search ${t('attendance.user').toLowerCase()}...`}
                                            title={`FILTER BY ${t('attendance.user').toUpperCase()}`}
                                            description="Results update as you type"
                                            isOpen={isFilterPopupOpen('user')}
                                            onOpenChange={(open) => handleFilterPopupChange('user', open)}
                                        />
                                    </div>
                                </TableHead>
                                <TableHead className={getTableHeadClasses('attendee_type')}>
                                    <div className="flex items-center">
                                        <span className={getTableHeadTextClasses('attendee_type')}>
                                            {t('attendance.attendee_type')}
                                        </span>
                                        <PopoverSingleSelectFilterComponent
                                            filterState={{
                                                filterKey: 'attendee_type',
                                                filter: filters,
                                                setFilter: updateFilter
                                            }}
                                            options={[
                                                {value: 'all', label: 'All'},
                                                {value: 'user', label: t('attendance.user_types.user')},
                                                {value: 'guest', label: t('attendance.user_types.guest')}
                                            ]}
                                            placeholder="Select attendee type"
                                            title="FILTER BY ATTENDEE TYPE"
                                            isOpen={isFilterPopupOpen('attendee_type')}
                                            onOpenChange={(open) => handleFilterPopupChange('attendee_type', open)}
                                        />
                                    </div>
                                </TableHead>
                                <TableHead className={`${getTableHeadClasses('event_date')} min-w-[140px]`}>
                                    <div className="flex items-center">
                                        <span className={getTableHeadTextClasses('event_date')}>
                                            {t('attendance.event_date')}
                                        </span>
                                        <PopoverDateFilterComponent
                                            filterState={{
                                                filterKey: 'event_date',
                                                filter: filters,
                                                setFilter: updateFilter
                                            }}
                                            placeholder={`Pick event date`}
                                            title={`FILTER BY EVENT DATE`}
                                            description="Select a date to filter attendances"
                                            isOpen={isFilterPopupOpen('event_date')}
                                            onOpenChange={(open) => handleFilterPopupChange('event_date', open)}
                                        />
                                    </div>
                                </TableHead>
                                <TableHead className={`${getTableHeadClasses('time_in')} min-w-[120px]`}>
                                    <div className="flex items-center">
                                        <span className={getTableHeadTextClasses('time_in')}>
                                            {t('attendance.time_in')}
                                        </span>
                                        <PopoverTimeFilterV2Component
                                            filterState={{
                                                filterKey: 'time_in',
                                                filter: filters,
                                                setFilter: updateFilter
                                            }}
                                            placeholder={`Pick time in`}
                                            title={`FILTER BY TIME IN`}
                                            description="Select a check-in time to filter"
                                            isOpen={isFilterPopupOpen('time_in')}
                                            onOpenChange={(open) => handleFilterPopupChange('time_in', open)}
                                        />
                                    </div>
                                </TableHead>
                                <TableHead className={`${getTableHeadClasses('time_out')} min-w-[120px]`}>
                                    <div className="flex items-center">
                                        <span className={getTableHeadTextClasses('time_out')}>
                                            {t('attendance.time_out')}
                                        </span>
                                        <PopoverTimeFilterV2Component
                                            filterState={{
                                                filterKey: 'time_out',
                                                filter: filters,
                                                setFilter: updateFilter
                                            }}
                                            placeholder={`Pick time out`}
                                            title={`FILTER BY TIME OUT`}
                                            description="Select a check-out time to filter"
                                            isOpen={isFilterPopupOpen('time_out')}
                                            onOpenChange={(open) => handleFilterPopupChange('time_out', open)}
                                        />
                                    </div>
                                </TableHead>
                                <TableHead className={`${getTableHeadClasses('rewarded_points')} min-w-[150px]`}>
                                    <div className="flex items-center">
                                        <span className={getTableHeadTextClasses('rewarded_points')}>
                                            Rewarded Points
                                        </span>
                                        <PopoverNumberFilterComponent
                                            filterState={{
                                                filterKey: 'rewarded_points',
                                                filter: filters,
                                                setFilter: updateFilter
                                            }}
                                            placeholder={`Enter points`}
                                            title={`FILTER BY REWARDED POINTS`}
                                            isOpen={isFilterPopupOpen('rewarded_points')}
                                            onOpenChange={(open) => handleFilterPopupChange('rewarded_points', open)}
                                        />
                                    </div>
                                </TableHead>
                                <TableHead className={getTableHeadClasses('status')}>
                                    <div className="flex items-center">
                                        <span className={getTableHeadTextClasses('status')}>
                                            {t('attendance.status')}
                                        </span>
                                        <PopoverSingleSelectFilterComponent
                                            filterState={{
                                                filterKey: 'status',
                                                filter: filters,
                                                setFilter: updateFilter
                                            }}
                                            options={[
                                                {value: 'all', label: 'All'},
                                                {value: 'signed_in', label: 'Checked in'},
                                                {value: 'signed_out', label: 'Checked out'},
                                                {value: 'registered', label: 'Attending'}
                                            ]}
                                            placeholder="Select status"
                                            title="FILTER BY STATUS"
                                            isOpen={isFilterPopupOpen('status')}
                                            onOpenChange={(open) => handleFilterPopupChange('status', open)}
                                        />
                                    </div>
                                </TableHead>
                            </TableRow>
                        </TableHeader>
                        <TableBody>
                            {loading ? (
                                <TableRow>
                                    <TableCell colSpan={8} className="text-center py-8">
                                        <div className="text-center">
                                            <div
                                                className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
                                            <p className="text-gray-600">{t('attendance.loading_attendances')}</p>
                                        </div>
                                    </TableCell>
                                </TableRow>
                            ) : attendances.length > 0 ? attendances.map((attendance, index) => (
                                <TableRow key={index}>
                                    <TableCell className="font-medium">
                                        <div className="flex items-center space-x-3">
                                            <img
                                                src={attendance.event_image_url}
                                                alt={attendance.event_name}
                                                className="w-10 h-10 rounded object-cover"
                                                onError={(e) => {
                                                    const target = e.target as HTMLImageElement;
                                                    target.src = 'https://citypointsblob.blob.core.windows.net/civic-points/system-images/no-image-vector-symbol-missing-available-icon-gallery-moment-placeholder-246411909_1765604291.webp';
                                                }}
                                            />
                                            <div>
                                                <div className="font-medium truncate max-w-[250px]"
                                                     title={attendance.event_name}>{attendance.event_name}</div>
                                                <div className="text-sm text-muted-foreground truncate max-w-[250px]"
                                                     title={attendance.event_location}>{attendance.event_location}</div>
                                            </div>
                                        </div>
                                    </TableCell>
                                    <TableCell>
                                        <div>
                                            <div className="font-medium">
                                                {attendance.user.first_name} {attendance.user.last_name}
                                            </div>
                                            <div className="text-sm text-muted-foreground">{attendance.user.email}</div>
                                        </div>
                                    </TableCell>
                                    <TableCell>
                                        {getAttendeeTypeBadge(attendance.user_type)}
                                    </TableCell>
                                    <TableCell>
                                        {formatDate(attendance.event_date)}
                                    </TableCell>
                                    <TableCell>
                                        {formatTime(attendance.user_time_in)}
                                    </TableCell>
                                    <TableCell>
                                        {formatTime(attendance.user_time_out)}
                                    </TableCell>
                                    <TableCell>
                                        <Badge variant="outline" className="font-medium">
                                            {attendance.point_reward || 0}
                                        </Badge>
                                    </TableCell>
                                    <TableCell className="text-sm">
                                        {getStatusBadge(attendance.attendance_status)}
                                    </TableCell>
                                </TableRow>
                            )) : (
                                <TableRow>
                                    <TableCell colSpan={8} className="text-center py-8">
                                        <p className="text-muted-foreground">
                                            {hasAnyActiveFilter() ? "No attendances found" : "Currently no attendances available."}
                                        </p>
                                    </TableCell>
                                </TableRow>
                            )}
                        </TableBody>
                    </Table>
                </div>
                {/* Pagination */}
                <CommonPagination
                    page={page}
                    totalPages={totalPages}
                    onPageChange={handlePageChange}
                />
            </div>
        </ScrollableContentLayout>
    );
};

export default AttendancePage;
